import * as Sentry from '@sentry/react'
import { initLogger } from '@paintscout/logger'
import history from 'shared/util/routing/history'

// Same split as apps/api's tracesSampler: dev/test get more headroom to
// validate against, production stays conservative since this is the first
// frontend trace/web-vitals data we've ever sent and there's no baseline yet.
const tracesSampleRate =
  process.env.DEPLOYMENT_STAGE === 'test' || process.env.DEPLOYMENT_STAGE?.startsWith('dev') ? 0.005 : 0.0001

Sentry.init({
  dsn: process.env.SENTRY_APP_DSN,
  release: `${process.env.VERSION}`,
  environment: process.env.DEPLOYMENT_STAGE,

  integrations: [Sentry.reactRouterV5BrowserTracingIntegration({ history })],
  tracesSampleRate,
  // Sentry only attaches trace headers to same-origin requests by default, and
  // app/customer-view call core-api on a different subdomain. Same wildcard
  // pattern apps/api's CORS config already trusts (serverless/custom.yml).
  // Excludes `auth.` — that's the Auth0 custom domain, whose CORS allow-list
  // we don't control and which rejects the sentry-trace/baggage headers this
  // adds, breaking the /oauth/token preflight and login itself (see login CORS
  // failure, 2026-09-22).
  tracePropagationTargets: [
    /^https:\/\/(?!auth\.)[^/]*\.paintscout\.com/,
    /^https:\/\/(?!auth\.)[^/]*\.paintscout\.dev/
  ],

  ignoreErrors: [
    /^typeError: failed to fetch$/i,
    /^typeError: cancelled$/i,
    /Cannot resolve a Slate point from DOM point/i,
    /newestWorker is null/i,
    /Invariant Violation: 8/i,
    /ceCurrentVideo.currentTime/i,
    /undefined is not an object .* evaluating .*length/i,
    /network/i,
    // this error gets thrown when device is offline, and is expected.
    /Failed to update a ServiceWorker for scope/i,
    // Third-party: react-sortable-hoc null reference on touch events (PAINTSCOUT-APP-B4M)
    /null is not an object \(evaluating '.*\.active\.collection'\)/i,
    /reading 'collection'/i,
    /evaluating 'this.active.collection'/i,
    /evaluating 'a.active.collection'/i,
    /this.active is null/i,
    /evaluating 'this.g.src'/i,
    /Script https:\/\/.*\.paintscout\.com\/sw\.js load failed/i,
    // Third-party: Userflow SDK loading failures
    /Could not load Userflow\.js/i,
    // Third-party: cross-origin frame access (e.g. Google Maps, iframes)
    /SecurityError.*cross-origin/i,
    // Browser extensions: chrome.runtime.sendMessage noise
    /runtime\.sendMessage\(\).*Tab not found/i,
    // Third-party: Userflow dynamic import failures
    /userflow/i,
    // Benign browser warning (PAINTSCOUT-APP-73Q)
    /ResizeObserver loop completed with undelivered notifications/i,
    // JWT token expiration — expected in long-lived tabs; Auth0 refresh handles recovery
    // (PAINTSCOUT-APP-99G, PAINTSCOUT-APP-C4J, PAINTSCOUT-APP-AVY, PAINTSCOUT-APP-ATV)
    /GraphQL error: jwt expired/i
  ],
  denyUrls: [
    // Userflow SDK
    /js\.userflow\.com/i,
    // Google Maps API
    /maps-api-v3/i,
    // Safari internal/masked URLs (WebKit extensions)
    /webkit-masked-url/i
  ],
  beforeSend(event, _) {
    // Fingerprint MUI Tabs null-ref errors together (PAINTSCOUT-APP-CAB, CAA, CAP)
    const errorValue = event.exception?.values?.[0]?.value ?? ''
    if (/scrollTop|offsetHeight|offsetWidth/.test(errorValue) && /null/.test(errorValue)) {
      event.fingerprint = ['mui-tabs-null-ref']
    }

    // Catch Non-Error exceptions and grab __serialized__ info and send as error instead, null if none present
    const isNonErrorException = (event.exception?.values?.[0]?.value ?? '').startsWith('Non-Error exception captured')
    if (isNonErrorException) {
      if (!event.extra.__serialized__) {
        return null
      }
      let realErrMsg = (event.extra.__serialized__ as any)?.error
        ? (event.extra.__serialized__ as any)?.error.message
        : null
      realErrMsg = realErrMsg || (event.extra.__serialized__ as any)?.message
      event.exception.values[0].value = realErrMsg
      event.message = realErrMsg
    }
    return event
  }
})

initLogger({
  sentry: Sentry,
  provider: 'app',
  stage: process.env.DEPLOYMENT_STAGE,
  version: process.env.VERSION
})

export { Sentry }
